import { useEffect } from "react";
import { fbAdd, fbUpdate, fbDelete, fbWatch } from "./useFirebase";
import { normalizarStatus } from "../constants/statusPedido";

const STATUS_FILA = { aguardando_impressao: "aguardando", imprimindo: "imprimindo" };

function tsMs(ts) {
  if (!ts) return 0;
  if (ts.toDate) return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return new Date(ts).getTime();
}

// Mantém a coleção fila_impressao espelhando o status de impressão dos pedidos,
// sem precisar de ação manual — a fila (aba Fila de Impressão) e as métricas
// do Dashboard (Horas impressas/Filamento usado/Taxa de sucesso) leem daqui.
export function useFilaSync(ativo) {
  useEffect(() => {
    if (!ativo) return;
    let pedidos = null, fila = null;
    let rodando = false, pendente = false;
    // pedidoId com fbAdd em voo — evita criar duplicado antes do snapshot ecoar de volta
    const emCriacao = new Set();

    const sincronizar = async () => {
      if (rodando) { pendente = true; return; }
      rodando = true;
      try {
        if (!pedidos || !fila) return;

        // Limpa duplicatas que já existam (mesmo pedidoId em mais de um doc) — mantém o mais antigo
        const porPedido = new Map();
        for (const item of fila) {
          if (!item.pedidoId || item.tentativa_encerrada) continue;
          if (!porPedido.has(item.pedidoId)) porPedido.set(item.pedidoId, []);
          porPedido.get(item.pedidoId).push(item);
        }
        for (const itens of porPedido.values()) {
          if (itens.length <= 1) continue;
          const ordenados = [...itens].sort((a, b) => tsMs(a.created_at) - tsMs(b.created_at));
          for (const extra of ordenados.slice(1)) {
            try { await fbDelete("fila_impressao", extra.id); } catch (e) { console.error("useFilaSync dedup:", e); }
          }
        }

        // Um item por pedido (o mais antigo, caso ainda reste duplicata não sincronizada)
        const porPedidoId = new Map();
        for (const item of fila) {
          if (!item.pedidoId || item.tentativa_encerrada) continue;
          const atual = porPedidoId.get(item.pedidoId);
          if (!atual || tsMs(item.created_at) < tsMs(atual.created_at)) porPedidoId.set(item.pedidoId, item);
        }

        for (const p of pedidos) {
          const st = normalizarStatus(p.status);
          const existente = porPedidoId.get(p.id);
          const filaSt = STATUS_FILA[st] || null;
          // Doc "existente" pode ter sumido (apagado em outro lugar, cache
          // local desatualizado...) sem o array `fila` em memória ainda ter
          // percebido — updateDoc nesse caso rejeita com "not-found" e, sem
          // esse fallback, cada sincronização repetia o mesmo erro pra
          // sempre (loop infinito gastando cota do Firestore à toa).
          // Recriar o doc quebra o loop: a próxima leitura de fila_impressao
          // já enxerga o novo doc com id válido.
          const atualizarOuRecriar = async (id, patch) => {
            try { await fbUpdate("fila_impressao", id, patch); }
            catch (e) {
              if (e?.code === "not-found") {
                // O array local ainda contém um item que já foi removido em
                // outro snapshot. Espera a próxima leitura da fila antes de
                // criar de novo, evitando alternar entre documentos.
                return;
              } else throw e;
            }
          };
          try {
            if (filaSt) {
              if (!existente && !emCriacao.has(p.id)) {
                emCriacao.add(p.id);
                await fbAdd("fila_impressao", { tipo: "pedido", pedidoId: p.id, nome: p.produto || "Pedido", status: filaSt, ordem: Date.now() });
              } else if (existente && existente.status !== filaSt && existente.status !== "concluido" && existente.status !== "falhou") {
                await atualizarOuRecriar(existente.id, { status: filaSt });
              }
            } else if (existente && existente.status !== "concluido" && existente.status !== "falhou") {
              if (st === "cancelado") await fbDelete("fila_impressao", existente.id).catch(e => { if (e?.code !== "not-found") throw e; });
              else await atualizarOuRecriar(existente.id, { status: "concluido" });
            }
          } catch (e) { console.error("useFilaSync:", e); }
        }
      } finally {
        rodando = false;
        if (pendente) { pendente = false; sincronizar(); }
      }
    };

    const unsubs = [
      fbWatch("pedidos", d => { pedidos = d; sincronizar(); }),
      fbWatch("fila_impressao", d => {
        fila = d;
        for (const item of d) if (item.pedidoId && !item.tentativa_encerrada) emCriacao.delete(item.pedidoId);
        sincronizar();
      }),
    ];
    return () => unsubs.forEach(u => u && u());
  }, [ativo]);
}
